import { getConfig } from '@leagueplatform/config';
import {
  captureError,
  combineErrorContexts,
} from '@leagueplatform/observability';
import type { ErrorContext } from '@leagueplatform/observability';
import {
  buildCustomHeaders,
  combineHeaders,
  getLeagueFetchOptions,
  getAcceptLanguageHeader,
  getMirrordRoute,
} from './utils';

interface FetchOptions extends RequestInit {
  errorContext?: ErrorContext;
  allowUnauthenticated?: boolean;
  api?: 'rest' | 'content';
  skipErrorCapture?: boolean;
}

export const leagueFetch = async (path: string, options: FetchOptions = {}) => {
  const {
    core: {
      api: { url: apiUrl },
      contentUrl,
      clientId,
      appVersion,
      customHeaders,
    },
  } = getConfig().config;
  // allowUnauthenticated is set to true when implementer wants to bypass auth for making REST calls.
  const {
    allowUnauthenticated,
    api = 'rest',
    errorContext: optionalErrorContext = {},
    skipErrorCapture,
    ...fetchOptions
  } = options;
  const leagueFetchOptions = await getLeagueFetchOptions(allowUnauthenticated);
  const getCurrentLocale = getAcceptLanguageHeader();
  const mirrordRoute = getMirrordRoute();
  const clientIdOptions = {
    headers: new Headers({
      // Exclude the 'X-League-Client-Id' header for content server fetches
      // TODO update this once the content server accepts this header
      ...(api === 'rest' ? { 'X-League-Client-Id': clientId } : {}),
      ...(appVersion && { 'X-League-Client-Version': appVersion }),
      ...(mirrordRoute && { 'X-League-Mirrord-Route': mirrordRoute }),
    }),
  };

  /* Dynamically select the endpoint to fetch from:
   * default to the core API URL, or else use the content
   * URL if specified in the fetch options (api = 'content')
   *
   * To work with the mock server, replace with the code below */
  // const url = path.match(/^http/)
  //   ? path
  //   : `${api === 'rest' ? apiUrl : contentUrl}${path.replace(/^\/?/, '/')}`;
  const url = `${api === 'rest' ? apiUrl : contentUrl}${path.replace(
    /^\/?/,
    '/',
  )}`;

  const promise = fetch(url, {
    ...fetchOptions,
    ...leagueFetchOptions,
    headers: combineHeaders(
      buildCustomHeaders(customHeaders),
      fetchOptions,
      leagueFetchOptions,
      getCurrentLocale,
      clientIdOptions,
    ),
  });

  const response = await promise;

  if (!response.ok) {
    let body: { errors?: Array<{ detail?: string }> } = {};
    let errorDetail = 'An error has occurred';
    try {
      const parsed: unknown = await response.clone().json();
      if (parsed !== null && typeof parsed === 'object') {
        body = parsed as typeof body;
        errorDetail = body.errors?.[0]?.detail ?? 'An error has occurred';
      }
    } catch {
      // Non-JSON body — keep defaults
    }
    const fetchErrorContext = combineErrorContexts([
      {
        errorName: 'Network Fetch Error',
        tags: { requestUrl: response.url },
        context: {
          Errors: {
            ...body.errors,
          },
          Response: {
            ok: response.ok,
            redirected: response.redirected,
            status: response.status,
            type: response.type,
            url: response.url,
            method: fetchOptions.method ?? 'GET',
          },
        },
      },
      optionalErrorContext,
    ]);
    if (!skipErrorCapture) {
      captureError(new Error(errorDetail), fetchErrorContext);
    }
  }

  return promise;
};
